import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  MutableRefObject,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useContext
} from 'react';
import ChatBot, {
  Flow,
  Params,
  Styles,
  Settings,
  useMessages,
} from 'react-chatbotify';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { NiKendoContext } from 'context/NiKendoContext';
import 'katex/dist/katex.min.css';
import {
  generateUUID,
  AIApplication,
  streamUserMessage,
  closeAIApiCallEventName,
  streamSmartTicketingMessage,
} from '../helper';
import { apiUrls } from 'constants/apiUrls';

import avatar from '../../../assets/images/ni-logo.svg';
import AuthService from 'services/AuthService';

export interface Ref {
  handleResetChat: () => void;
}
interface Props {
  id: string;
  newTab?: boolean;
  aiApplication: AIApplication;
  activeApplication: AIApplication;
  eventSourceRef: MutableRefObject<EventSource | null>;
  handleClose: () => void;
}

export const getStyles = (newTab: boolean): Styles => ({
  bodyStyle: {
    height: '90%',
  },
  chatWindowStyle: {
    width: '100%',
    height: newTab ? '70vh' : '60vh',
  },
  userBubbleStyle: {
    color: 'var(--color-main)',
    backgroundColor: 'var(--color-primary-accent)',
    maxWidth: '80%',
  },
  botBubbleStyle: {
    display: 'flex',
    color: 'var(--color-contrast)',
    backgroundColor: 'var(--chatbot-bg)',
    maxWidth: '80%',
  },
  sendButtonStyle: {
    color: 'var(--color-main)',
    backgroundColor: 'var(--color-primary-accent)',
  },
  headerStyle: {
    alignItems: 'center',
    backgroundImage: 'none',
    color: 'var(--color-main)',
    backgroundColor: 'var(--color-primary-accent)',
  },
});

export const settings: Settings = {
  general: {
    desktopEnabled: true,
    fontFamily: "'Open Sans', sans-serif",
    primaryColor: 'var(--color-primary-accent)',
    showFooter: false,
    embedded: true,
    showHeader: false,
  },
  botBubble: {
    simStream: true,
    showAvatar: true,
    streamSpeed: 0,
    dangerouslySetInnerHtml: true,
    avatar: avatar,
  },
  header: {
    title: 'AI Assistant',
    buttons: [],
  },
  chatHistory: {
    //I tried to enable the history but it is not working as expected for multiple chatbot instances
    disabled: true,
  },
  chatWindow: {
    showScrollbar: true,
  },
  chatInput: {
    enabledPlaceholderText: 'Ask here',
  },
  voice: {
    disabled: false,
    autoSendDisabled: true,
    language: 'fr-FR',
    timeoutPeriod: 30000,
  },
};

const CHAT_HISTORY_KEY = 'chathistory-';

const loadFromStorage = (aiApplication: AIApplication) => {
  const user = AuthService.getUser();
  const stored = localStorage.getItem(CHAT_HISTORY_KEY + aiApplication + user?.profile.sub);
  const sessionId = localStorage.getItem('sessionId' + aiApplication + user?.profile.sub);
  return stored ? { messages: JSON.parse(stored), sessionId} : { messages: [], sessionId: null };
};

const clearStorage = (aiApplication: AIApplication) => {
  const user = AuthService.getUser();
  localStorage.removeItem(CHAT_HISTORY_KEY + aiApplication + user?.profile.sub);
  localStorage.removeItem('sessionId' + aiApplication + user?.profile.sub);
};

const saveToStorage = (messages: any[], sessionId: string, aiApplication: AIApplication) => {
  const user = AuthService.getUser();
  localStorage.setItem(CHAT_HISTORY_KEY + aiApplication + user?.profile.sub, JSON.stringify(messages));
  localStorage.setItem('sessionId' + aiApplication + user?.profile.sub, sessionId);
};

export const getFlow = (
  eventSourceRef: MutableRefObject<EventSource | null>,
  aiApplication: AIApplication,
  sessionId: MutableRefObject<string | null>,
  selectedModule: string | null,
  setSelectedModule: (module: string) => void,
  handleDashboardClick: (dashboardData: any | null) => void
): Flow => {
  return {
    start: {
      message: async (params: Params) => {
        var storedData = loadFromStorage(aiApplication);
        if (storedData.messages.length > 0) return;
        return 'How can I help you?';
      },
      path: 'model_loop',
    },
    model_loop: {
      message: async (params: Params) => {
        try {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          } else {
            let url = '';

            switch (aiApplication) {
              case AIApplication.SMART_TICKETING:
                url = `${apiUrls.chatbotUrl}?message=${params.userInput}&application=${aiApplication}&sessionId=${sessionId.current}`;

                break;
              case AIApplication.USER_GUIDE:
                url = `${apiUrls.chatbotUrl}?message=${params.userInput}&application=${aiApplication}`;
                break;
              case AIApplication.INTEGRATION_ASSISTANT:
                url = `${apiUrls.chatbotUrl}?message=${params.userInput}&application=${aiApplication}&sessionId=${sessionId.current}`;
                console.log(url);
                break;
              case AIApplication.ASSISTANT:
                url = `${apiUrls.chatbotUrl}?message=${params.userInput}&application=${aiApplication}&sessionId=${sessionId.current}`;
                break;
              default:
                url = `${apiUrls.chatbotUrl}?message=${params.userInput}&application=${aiApplication}&sessionId=${sessionId.current}`;
                break;
            }
            eventSourceRef.current = new EventSource(url);

            let accumulatedMessage = '';

            await new Promise<void>((resolve, reject) => {
              //This listener is to close the connection of the EventSource
              eventSourceRef.current!.addEventListener(
                closeAIApiCallEventName,
                (e: MessageEvent<any>) => {
                  eventSourceRef.current?.close();

                  setTimeout(() => {
                    eventSourceRef.current = null;
                  }, 100);
                  resolve();
                }
              );
              eventSourceRef.current!.onmessage = async (
                e: MessageEvent<any>
              ) => {
                try {
                  switch (aiApplication) {
                    case AIApplication.SMART_TICKETING: {
                      accumulatedMessage = await streamSmartTicketingMessage(
                        e,
                        accumulatedMessage,
                        params,
                        eventSourceRef
                      );
                      break;
                    }
                    case AIApplication.USER_GUIDE:
                    case AIApplication.INTEGRATION_ASSISTANT:
                    case AIApplication.ASSISTANT: {
                      accumulatedMessage = await streamUserMessage(
                        e,
                        accumulatedMessage,
                        params,
                        eventSourceRef,
                        selectedModule,
                        handleDashboardClick
                      );
                      break;
                    }

                    default:
                      break;
                  }
                  // Resolve the promise when you are done processing the event
                  resolve();
                } catch (error) {
                  await params.injectMessage('Something went wrong!');
                  console.error(error);
                  reject(error); // Reject the promise in case of an error
                }
              };
            });
          }
        } catch (error) {
          await params.injectMessage('Something went wrong!');
          console.error('Error fetching streamable data:', error);
        }
      },
      path: 'model_loop',
    },
  };
};

const welcomeMessage = `How can I help you?`;

const AIChatbot: ForwardRefRenderFunction<Ref, Props> = (
  {
    id,
    newTab = false,
    eventSourceRef,
    aiApplication,
    activeApplication,
    handleClose,
  },
  ref
) => {
  const { messages, removeMessage, setMessages, streamMessage, injectMessage } =
    useMessages();
  const lastResetChat = useRef<boolean>(false);
  const sessionId = useRef<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [resetChat, setResetChat] = useState<boolean>(false);
  const navigate = useNavigate();
  const { niKendoModule } = useContext(NiKendoContext);

  const handleResetChat = () => {
    sessionId.current = generateUUID();
    setResetChat((prev) => !prev);
    lastResetChat.current = resetChat;
  };

  const handleDashboardClick = (dashboardData: any | null) => {
    try {
      if (dashboardData) {
        const dashboard = JSON.parse(dashboardData);
        if (
          niKendoModule.application.views[dashboard.dashboardId] &&
          dashboard.dataFilters
        ) {
          niKendoModule.application.views[
            dashboard.dashboardId
          ].setParentDashboardSyncData(dashboard.dataFilters);
        }
        navigate(`/view/${dashboard.dashboardId}`);
        // close here
        handleClose();
      }
    } catch (error) {
      streamMessage(dashboardData, 'bot');
    }
  };

  const handleNewChat = () => {
    clearStorage(aiApplication);
    messages.forEach((el) => {
      removeMessage(el.id);
    });

    // For other applications, reset with a simple welcome message
    setMessages([
      {
        id: generateUUID(),
        content: welcomeMessage,
        sender: 'bot',
        timestamp: dayjs().unix().toString(),
        type: '',
      },
    ]);
  };

  useImperativeHandle(ref, () => ({
    handleResetChat,
  }));

  useEffect(() => {
    sessionId.current = generateUUID();
    const storedData = loadFromStorage(aiApplication);
    if (storedData.messages && storedData.messages.length > 0) {
      sessionId.current = storedData.sessionId || sessionId.current;
      storedData.messages.forEach((msg: any) => {
        if (msg.content.type == 'button') {
          const dashboardData = msg.content.props.dashboardData;
          const buttonElement = React.createElement(
            'button',
            {
              dashboardData: dashboardData,
              className: 'chatOpenDashboard',
              onClick: () => handleDashboardClick(dashboardData),
            },
            'Open Report'
          );
          injectMessage(buttonElement, msg.sender);
        } else injectMessage(msg.content, msg.sender);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastResetChat.current !== resetChat) {
      handleNewChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetChat]);

  useEffect(() => {
    if (messages.length === 0) return;
    saveToStorage(messages,  sessionId.current || "", aiApplication);
  }, [messages, aiApplication]);

  return (
    <div
      style={{
        display: activeApplication === aiApplication ? 'block' : 'none',
      }}
    >
      <ChatBot
        id={id}
        settings={settings}
        flow={getFlow(
          eventSourceRef,
          aiApplication,
          sessionId,
          selectedModule,
          setSelectedModule,
          handleDashboardClick
        )}
        styles={getStyles(newTab)}
      />
    </div>
  );
};

export default forwardRef<Ref, Props>(AIChatbot);
